// src/ui/bubbles/Bubble.tsx — Phase 6 Plan 04 single bubble unit.
//
// UI-SPEC §Bubble verbatim:
//   - role="status", aria-live="polite", aria-atomic="true"
//   - typewriter at P6_BUBBLE_TYPEWRITER_CPS (28 cps) with .?! +120ms /
//     , +40ms punctuation pauses
//   - wobble keyframe runs once 600ms cubic-bezier on mount
//   - prefers-reduced-motion → instant text reveal, no wobble (CSS handles
//     wobble disable; JS handles instant text on first render)
//   - position composited via --bx/--by CSS custom properties — bubbleRef
//     prop lets BubbleLayer's rAF loop write directly without re-rendering
//     this component.
//
// XSS guard (T-6-04-01): text is rendered via React `{displayed}` only;
// React 19 escapes interpolated children automatically. The raw-HTML escape
// hatch is intentionally never used in this file (regression-asserted by
// the bubbles/ grep gate in 06-04-PLAN acceptance criteria).

import { useEffect, useRef, useState, type JSX, type RefObject } from 'react';

import {
  P6_BUBBLE_TYPEWRITER_CPS,
  P6_BUBBLE_TYPEWRITER_PUNCT_PAUSE_MS,
  P6_BUBBLE_TYPEWRITER_COMMA_PAUSE_MS,
} from '../../render/audio/constants';
import type { BubbleTier } from './bubble-priority';

import './bubbles.css';

export type BubbleTailDirection = 'down' | 'up';

export interface BubbleProps {
  text: string;
  tier: BubbleTier;
  /** Indicates this bubble was triggered by a 'foxguard-called' event so the
   *  CSS coral border modifier (`bubble--foxguard`) can override the default
   *  amber critical-tier border. Declared as `T | undefined` (not `T?`) for
   *  exactOptionalPropertyTypes compatibility — explicit-undefined assignments
   *  from BubbleLayer compile cleanly. */
  foxguard?: boolean | undefined;
  reducedMotion: boolean;
  bubbleRef: RefObject<HTMLDivElement | null>;
  /** When true, the bubble fades out via the .bubble--preempted modifier. */
  preempted?: boolean | undefined;
  tailDirection?: BubbleTailDirection | undefined;
}

export function Bubble({
  text,
  tier,
  foxguard = false,
  reducedMotion,
  bubbleRef,
  preempted = false,
  tailDirection = 'down',
}: BubbleProps): JSX.Element {
  const [displayed, setDisplayed] = useState<string>(reducedMotion ? text : '');
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (reducedMotion) {
      setDisplayed(text);
      return;
    }
    cancelledRef.current = false;
    let i = 0;
    const charMs = 1000 / P6_BUBBLE_TYPEWRITER_CPS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    function next(): void {
      if (cancelledRef.current) return;
      if (i >= text.length) return;
      const ch = text[i];
      i += 1;
      setDisplayed(text.slice(0, i));
      let extra = 0;
      if (ch === '.' || ch === '?' || ch === '!') {
        extra = P6_BUBBLE_TYPEWRITER_PUNCT_PAUSE_MS;
      } else if (ch === ',') {
        extra = P6_BUBBLE_TYPEWRITER_COMMA_PAUSE_MS;
      }
      timer = setTimeout(next, charMs + extra);
    }
    timer = setTimeout(next, charMs);
    return (): void => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [text, reducedMotion]);

  const classNames = [
    'bubble',
    `bubble--${tier}`,
    `bubble--tail-${tailDirection}`,
  ];
  if (foxguard) classNames.push('bubble--foxguard');
  if (preempted) classNames.push('bubble--preempted');

  return (
    <div
      ref={bubbleRef}
      className={classNames.join(' ')}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid={`bubble-${tier}`}
      data-foxguard={foxguard ? 'true' : undefined}
    >
      {displayed}
    </div>
  );
}
